/**
 * HLOS-27 — Wrapper BIP-39 mnemonic TS (espelha `hlos/vault/mnemonic.py`).
 *
 * Port TS literal de `mnemonic` (Python lib). Usa `@scure/bip39` (mantida
 * por Paul Miller, auditada Cure53, ~10KB minified).
 *
 * **Paridade byte-a-byte com Python** (mnemonic lib v0.21+):
 *   - Mesmas palavras (English BIP-39 wordlist, 2048 palavras)
 *   - Mesmos test vectors oficiais BIP-39 (Trezor) — validados em `__tests__`
 *   - Mesmo algoritmo PBKDF2-HMAC-SHA512 (2048 rounds) em `mnemonicToSeed`
 *
 * Por que @scure/bip39 (não js-sha256, noble, etc.):
 *   - Auditada por Cure53 (security firm líder)
 *   - ~10KB minified (tree-shake friendly — só English wordlist bundled)
 *   - Sem dependência transitiva pesada
 *   - Compatível com Python `mnemonic` lib (test vectors oficiais)
 *
 * Garantias Constitucionais:
 *   - **CL-07** (Modelo = ativo): geração de entropia local via CSPRNG
 *   - **CL-08** (Ética fechada): mnemonic nunca sai do device plaintext
 *     após uso (caller deve wipear seed/mnemonic via `secureWipe()`)
 *   - **Princípio X** (Dignidade): UX clara sobre保管 do mnemonic (aviso
 *     único, sem nag) — implementado no consumer (OnboardingState)
 *
 * **BIP-39 PBKDF2 passphrase**: age como 25ª palavra. Vazia = sem extra.
 *
 * Referência: `docs/architecture/IDENTITY_AND_KEY_MANAGEMENT.md` §3.1.
 */

import {
  generateMnemonic as scureGenerate,
  mnemonicToSeedSync as scureToSeed,
  validateMnemonic as scureValidate,
  entropyToMnemonic as scureEntropyToMnemonic,
  mnemonicToEntropy as scureMnemonicToEntropy,
} from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english.js";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes (espelham Python — KEEP IN SYNC)
// ─────────────────────────────────────────────────────────────────────────────

/** Strengths BIP-39 suportadas (em bits). */
export const VALID_STRENGTHS = [128, 160, 192, 224, 256] as const;
export type ValidStrength = (typeof VALID_STRENGTHS)[number];

/** Wordlist (default: English). Apenas English bundled (tree-shake). */
export const DEFAULT_WORDLIST = "english" as const;

/** Passphrase adicional BIP-39 (25ª palavra). Vazio = sem extra. */
export const BIP39_PASSPHRASE_DEFAULT = "" as const;

/** Tamanho do seed derivado (512 bits = 64 bytes). */
export const SEED_BYTES = 64;

// ─────────────────────────────────────────────────────────────────────────────
// Erros (espelham Python `exceptions.InvalidMnemonicError`)
// ─────────────────────────────────────────────────────────────────────────────

export class InvalidMnemonicError extends Error {
  constructor(message: string = "mnemonic failed BIP-39 checksum or contains unknown words") {
    super(message);
    this.name = "InvalidMnemonicError";
  }
}

export class InvalidStrengthError extends Error {
  constructor(actualStrength: number) {
    super(
      `BIP-39 strengthBits must be one of ${VALID_STRENGTHS.join("/")}, got ${actualStrength}`,
    );
    this.name = "InvalidStrengthError";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// generateMnemonic
// ─────────────────────────────────────────────────────────────────────────────

export function generateMnemonic(
  strengthBits: ValidStrength = 256,
): string {
  if (!VALID_STRENGTHS.includes(strengthBits)) {
    throw new InvalidStrengthError(strengthBits);
  }
  return scureGenerate(wordlist, strengthBits);
}

// ─────────────────────────────────────────────────────────────────────────────
// validateMnemonic
// ─────────────────────────────────────────────────────────────────────────────

export function validateMnemonic(mnemonic: string): boolean {
  try {
    return scureValidate(mnemonic, wordlist);
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// entropyToMnemonic / mnemonicToEntropy
// ─────────────────────────────────────────────────────────────────────────────

export function entropyToMnemonic(entropy: Uint8Array): string {
  const strength = entropy.length * 8;
  if (!VALID_STRENGTHS.includes(strength as ValidStrength)) {
    throw new InvalidStrengthError(strength);
  }
  return scureEntropyToMnemonic(entropy, wordlist);
}

export function mnemonicToEntropy(mnemonic: string): Uint8Array {
  if (!validateMnemonic(mnemonic)) {
    throw new InvalidMnemonicError();
  }
  return scureMnemonicToEntropy(mnemonic, wordlist);
}

// ─────────────────────────────────────────────────────────────────────────────
// mnemonicToSeed
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Deriva seed de 512 bits do mnemonic via PBKDF2-HMAC-SHA512 (2048 rounds).
 * Síncrono (espelha Python `mnemonic_to_seed()`).
 *
 * **CL-08**: caller DEVE wipear o seed retornado após uso via `secureWipe()`.
 */
export function mnemonicToSeed(
  mnemonic: string,
  passphrase: string = BIP39_PASSPHRASE_DEFAULT,
): Uint8Array {
  if (!validateMnemonic(mnemonic)) {
    throw new InvalidMnemonicError();
  }
  return scureToSeed(mnemonic, passphrase);
}

// ─────────────────────────────────────────────────────────────────────────────
// secureWipe
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Best-effort wipe de buffer. JS não garante zeroização (V8 mantém refs);
 * chamada zera in-place como defesa em profundidade, NÃO como garantia.
 */
export function secureWipe(buf: Uint8Array): void {
  buf.fill(0);
}
